import { StepCard } from '../../../step/StepCard';
import { ResizePanel } from '@scale/scaleui';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { SimpleTextCollectionCore } from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/SimpleTextCollectionCore';
import {
  TextCollectionFieldType,
  TextCollectionField,
  CategoryChoiceValue,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionResponse,
  CategoryFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { ChatTaskContextType } from '@scale/shared/types/chat';

// Define the feedback grade enum
export enum FeedbackGradeEnum {
  Complete = 'complete',
  Partial = 'partial',
  Unused = 'unused',
}

const formatSuggestedAction = (action: string): string => {
  return action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

type FeedbackCardProps = {
  feedback: {
    feedback_message: string;
    suggested_action?: string;
  };
  index: number;
  response: TextCollectionResponse;
  setResponse: (response: TextCollectionResponse) => void;
  readOnly?: boolean;
  chatMessageDisplayConfig: any;
  taskContext: ChatTaskContextType;
  enableRTL?: boolean;
};

export const FeedbackCard = ({
  feedback,
  index,
  response,
  setResponse,
  readOnly,
  chatMessageDisplayConfig,
  taskContext,
  enableRTL,
}: FeedbackCardProps) => {
  // Get the current rating value
  const ratingAnnotation = response?.annotations?.[
    `feedback_grade_${index}`
  ] as CategoryFieldAnnotation;
  const currentRating = ratingAnnotation?.response?.[0]?.[0] as CategoryChoiceValue;
  const showJustification =
    currentRating &&
    typeof currentRating === 'string' &&
    [FeedbackGradeEnum.Partial, FeedbackGradeEnum.Unused].includes(
      currentRating.toLowerCase() as FeedbackGradeEnum,
    );

  // Handle rating change to clear justification if needed
  const handleResponseChange = (newResponse: TextCollectionResponse) => {
    const newRatingAnnotation = newResponse?.annotations?.[
      `feedback_grade_${index}`
    ] as CategoryFieldAnnotation;
    const newRating = newRatingAnnotation?.response?.[0]?.[0] as CategoryChoiceValue;

    // If rating is changed to Complete, clear the justification
    if (
      newRating &&
      typeof newRating === 'string' &&
      newRating.toLowerCase() === FeedbackGradeEnum.Complete
    ) {
      const updatedResponse: TextCollectionResponse = {
        ...newResponse,
        annotations: {
          ...newResponse.annotations,
          [`feedback_justification_${index}`]: {
            type: TextCollectionFieldType.Text,
            field_id: `feedback_justification_${index}`,
            response: [''],
          },
        },
      };
      setResponse(updatedResponse);
      return;
    }

    setResponse(newResponse);
  };

  const fields: TextCollectionField[] = [
    {
      type: TextCollectionFieldType.Category,
      field_id: `feedback_grade_${index}`,
      title: `Rating`,
      choices: [
        { value: FeedbackGradeEnum.Complete, label: 'Complete' },
        { value: FeedbackGradeEnum.Partial, label: 'Partial' },
        { value: FeedbackGradeEnum.Unused, label: 'Unused' },
      ],
      required: true,
    },
    ...(showJustification
      ? [
          {
            type: TextCollectionFieldType.Text,
            field_id: `feedback_justification_${index}`,
            title: `Justification`,
            required: true,
          } as const,
        ]
      : []),
  ];

  return (
    <StepCard className="mb-4">
      <StepCard.SubHeader>
        <StepCard.HeaderLeft>
          <StepCard.SubHeaderTitle>Feedback {index + 1}</StepCard.SubHeaderTitle>
        </StepCard.HeaderLeft>
      </StepCard.SubHeader>
      <StepCard.Body>
        <ResizePanel
          left={
            <div className="p-4">
              <div className="text-sm text-neutral-700">
                <ChatMarkdown
                  className="border-none p-1"
                  markdown={feedback.feedback_message}
                  enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                  enableSingleDollarTextMath={
                    chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                  }
                  enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                  enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                  enableExecutableCodeBlocks={
                    taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                    taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                  }
                  enableRTL={enableRTL}
                />
              </div>
              {feedback.suggested_action && (
                <div className="mt-4 pt-4 border-t border-neutral-100">
                  <div className="font-medium text-neutral-900 mb-2">Suggested Action</div>
                  <div className="text-sm font-medium text-neutral-700">
                    {formatSuggestedAction(feedback.suggested_action)}
                  </div>
                </div>
              )}
            </div>
          }
          right={
            <SimpleTextCollectionCore
              params={{
                attachments: [],
                fields,
                enable_rtl_text: enableRTL,
              }}
              response={response}
              setResponse={handleResponseChange}
              readOnly={readOnly}
              readOnlyMessage="This section is read-only."
            />
          }
        />
      </StepCard.Body>
    </StepCard>
  );
};
